import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildHubwayzLocationFromDestination,
  isSelectableHubwayzCatalogDestination,
} from "../../../../../features/hubwayzHotelsTab/hubwayzDestinationOptions";
import { searchHubwayzDestinationCatalog } from "../../../../../services/hubwayzHotelsApi";
import "./autocompleteinput.scss";

const DEBOUNCE_MS = 280;
const QUERY_MAX = 64;

function optionKey(dest) {
  return [dest.countryCode, dest.hubwayzLocationId, dest.entityType].join(":");
}

/**
 * Hotels-tab destination autocomplete.
 * Suggestions come from the HotelsRiver catalog search, not a hardcoded city list.
 */
export default function HubwayzDestinationAutocomplete({
  location,
  saveLocationSelection,
  saveLocationEdit,
  closeInput,
}) {
  const inputRef = useRef(null);
  const requestSeq = useRef(0);
  const listId = useId();
  const [inputValue, setInputValue] = useState(
    location?.fullname || location?.name || location?.city || ""
  );
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [statusText, setStatusText] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const debounceRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const isSmallMobile = window.innerWidth <= 480;
    let dropdownWidth;
    let left;
    if (isSmallMobile) {
      dropdownWidth = window.innerWidth - 24;
      left = 12;
    } else if (isMobile) {
      dropdownWidth = window.innerWidth - 32;
      left = 16;
    } else {
      dropdownWidth = Math.max(rect.width * 1.5, 400);
      const maxLeft = window.innerWidth - dropdownWidth - 16;
      left = Math.min(rect.left + window.scrollX, maxLeft);
      left = Math.max(left, 16);
    }
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 6,
      left,
      width: dropdownWidth,
    });
  }, []);

  const runSearch = useCallback(
    async (raw) => {
      const value = String(raw || "").trim().slice(0, QUERY_MAX);
      if (value.length < 2) {
        setOptions([]);
        setShowDropdown(false);
        setStatusText("");
        setSelectedIndex(-1);
        return;
      }
      const seq = ++requestSeq.current;
      setStatusText("מחפש יעדים…");
      setShowDropdown(true);
      updateDropdownPosition();
      try {
        const data = await searchHubwayzDestinationCatalog(value);
        if (seq !== requestSeq.current) return;
        const results = (Array.isArray(data?.results) ? data.results : []).filter(
          isSelectableHubwayzCatalogDestination
        );
        setOptions(results);
        setSelectedIndex(results.length ? 0 : -1);
        setStatusText(results.length ? "" : "לא נמצא יעד. בחרו יעד מהרשימה.");
        updateDropdownPosition();
        setShowDropdown(true);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setOptions([]);
        setSelectedIndex(-1);
        const status = err?.response?.status;
        setStatusText(
          status === 429
            ? "יותר מדי חיפושים. נסו שוב בעוד רגע."
            : "לא ניתן לטעון יעדים כרגע."
        );
        setShowDropdown(true);
      }
    },
    [updateDropdownPosition]
  );

  const handleInputChange = useCallback(
    (e) => {
      const value = String(e.target.value || "").slice(0, QUERY_MAX);
      setInputValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void runSearch(value);
      }, DEBOUNCE_MS);
    },
    [runSearch]
  );

  const selectDestination = useCallback(
    (dest) => {
      const place = buildHubwayzLocationFromDestination({
        label: dest.label || dest.name,
        name: dest.name,
        hubwayzNameHint: dest.seoName || dest.name,
        seoName: dest.seoName,
        countryCode: dest.countryCode,
        countryLabel: dest.countryName || dest.countryNameEn,
        hubwayzLocationId: dest.hubwayzLocationId,
        destinationType: dest.destinationType === "hotel" ? "hotel" : "location",
        entityType: dest.entityType,
      });
      if (!isSelectableHubwayzCatalogDestination(dest)) return;
      setInputValue(place.fullname || place.name || "");
      setShowDropdown(false);
      setStatusText("");
      saveLocationEdit?.(false);
      saveLocationSelection?.(place);
      closeInput?.(false);
    },
    [saveLocationEdit, saveLocationSelection, closeInput]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        setShowDropdown(false);
        setSelectedIndex(-1);
        return;
      }
      if (!showDropdown || options.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const index = selectedIndex >= 0 ? selectedIndex : 0;
        selectDestination(options[index]);
      }
    },
    [showDropdown, options, selectedIndex, selectDestination]
  );

  useEffect(() => {
    if (location?.fullname) setInputValue(location.fullname);
    else if (location?.name) setInputValue(location.name);
  }, [location?.fullname, location?.name]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const onScroll = () => updateDropdownPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [showDropdown, updateDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        const dropdown = document.querySelector(".autocomplete-dropdown--hubwayz");
        if (dropdown && !dropdown.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeId =
    selectedIndex >= 0 ? `${listId}-opt-${selectedIndex}` : undefined;

  const dropdownContent = showDropdown ? (
    <div
      id={listId}
      className="autocomplete-dropdown autocomplete-dropdown--hubwayz"
      role="listbox"
      dir="rtl"
      style={{
        position: "fixed",
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        zIndex: 100000100,
      }}
    >
      {options.length > 0
        ? options.map((dest, index) => (
            <div
              key={optionKey(dest)}
              id={`${listId}-opt-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={`autocomplete-item ${index === selectedIndex ? "selected" : ""}`}
              onClick={() => selectDestination(dest)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="autocomplete-item-main-text">{dest.label || dest.name}</div>
              <div className="autocomplete-item-secondary-text">
                {[
                  dest.countryName || dest.countryNameEn || dest.countryCode,
                  dest.disambiguator || null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          ))
        : (
            <div className="autocomplete-hubwayz-hint" role="status">
              {statusText || "לא נמצא יעד. בחרו יעד מהרשימה."}
            </div>
          )}
    </div>
  ) : null;

  return (
    <>
      <div className="autoComplete-input-box-wrapper">
        <div className="autoComplete-input-box-container">
          <input
            ref={inputRef}
            type="text"
            placeholder="הקלידו יעד"
            className="autoComplete-input-box"
            dir="auto"
            value={inputValue}
            maxLength={QUERY_MAX}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            required
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            aria-controls={listId}
            aria-activedescendant={activeId}
            data-testid="hubwayz-destination-input"
          />
        </div>
      </div>
      {dropdownContent ? createPortal(dropdownContent, document.body) : null}
    </>
  );
}
